import { Link, NavLink, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X, ShoppingCart, Sparkles, Smartphone, UserRound } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { useClassRole } from "@/lib/access";
import { TAGLINE } from "@/lib/offer";

const NAV = [
  { to: "/", label: "Home", hi: "होम" },
  { to: "/vision", label: "Director's Vision", hi: "निदेशक का विज़न" },
  { to: "/programs/ai", label: "AI", hi: "AI" },
  { to: "/programs/robotics", label: "Robotics", hi: "रोबोटिक्स" },
  { to: "/programs/uav", label: "UAV", hi: "ड्रोन" },
  { to: "/programs/aerospace", label: "Aerospace", hi: "एयरोस्पेस" },
  { to: "/learn-and-earn", label: "Learn & Earn", hi: "सीखें और कमाएँ" },
  { to: "/virtual-classroom", label: "Virtual Class", hi: "वर्चुअल क्लास" },
  { to: "/robot-lab", label: "Robot Lab", hi: "रोबोट लैब" },
  { to: "/advanced-courses", label: "Advanced Courses", hi: "एडवांस्ड कोर्स" },
  { to: "/simran-live", label: "Simran Live", hi: "सिमरन लाइव", enrolledOnly: true },
  { to: "/schools", label: "Schools", hi: "स्कूल" },
  { to: "/roboshop", label: "Roboshop", hi: "रोबोशॉप" },
  { to: "/projects", label: "Projects", hi: "प्रोजेक्ट्स" },
  { to: "/contact", label: "Contact", hi: "संपर्क" },
];

const LangToggle = ({ isDark }) => {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex rounded-full border text-xs font-bold overflow-hidden ${isDark ? "border-slate-300" : "border-slate-300"}`} data-testid="lang-toggle">
      {[["en", "EN"], ["hi", "हिं"]].map(([k, l]) => (
        <button key={k} onClick={() => setLang(k)} data-testid={`lang-${k}`}
          className={`px-3 h-9 sm:h-7 sm:px-2.5 transition-colors ${lang === k ? "bg-[#FF3B3B] text-white" : ""}`}>{l}</button>
      ))}
    </div>
  );
};

export default function Header() {
  const [open, setOpen] = useState(false);
  const { count } = useCart();
  const { L } = useLang();
  const { pathname } = useLocation();
  const role = useClassRole();
  const nav = NAV.filter((n) => !n.enrolledOnly || role === "enrolled");
  const student = Boolean(localStorage.getItem("rs_student_token"));
  const portal = student ? "/student" : "/student/login";
  const isDark = ["/", "/vision", "/programs", "/learn-and-earn", "/schools", "/projects", "/leaderboard"].some(
    (p) => pathname === p || pathname.startsWith(p + "/")
  );

  const base = "bg-white/75 border-b border-slate-200 text-[#0A192F] shadow-[0_4px_24px_-12px_rgba(10,25,47,0.18)]";

  return (
    <header className={`sticky top-0 z-50 backdrop-blur-xl ${base}`} data-testid="site-header">
      <div className="max-w-[1536px] mx-auto px-5 lg:px-8 flex items-center justify-between gap-6 h-16">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="brand-link">
          <div className="relative w-8 h-8 rounded-md bg-[#FF3B3B] grid place-items-center shadow-[0_8px_24px_-6px_rgba(255,46,99,0.6)]">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-extrabold tracking-tight text-base">RoboSpark</span>
            <span className="text-[9px] uppercase tracking-[0.2em] opacity-70">PrP Pvt Ltd</span>
            <span className="text-[10px] font-bold italic text-[#FF2E63] mt-0.5 whitespace-nowrap" data-testid="header-tagline">{L(...TAGLINE)}</span>
          </div>
        </Link>

        <nav className="hidden min-[1440px]:flex items-center gap-3">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              className={({ isActive }) =>
                `whitespace-nowrap text-[13px] font-medium transition-colors ${
                  isActive ? "text-[#FF2E63]" : "text-slate-700 hover:text-[#0A192F]"
                }`
              }
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              {L(n.label, n.hi)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 shrink-0">
          <LangToggle isDark={isDark} />
          <Link to="/roboshop" className="relative p-2.5 sm:p-2 rounded-md hover:bg-black/5" data-testid="cart-icon">
            <ShoppingCart className="w-5 h-5" />
            {count > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#FF3B3B] text-white text-[10px] rounded-full w-5 h-5 grid place-items-center font-bold">
                {count}
              </span>
            )}
          </Link>
          <Link to="/get-app" className={`hidden sm:inline-flex items-center gap-1 whitespace-nowrap text-xs font-bold px-3 py-1.5 rounded-full border ${isDark ? "border-slate-300 hover:bg-slate-900/5" : "border-slate-300 hover:bg-slate-900/5"}`} data-testid="header-get-app-btn">
            <Smartphone className="w-4 h-4" />{L("Get App", "ऐप")}
          </Link>
          <Link to={portal} className={`hidden sm:inline-flex items-center gap-1 whitespace-nowrap text-xs font-bold px-3 py-1.5 rounded-full border border-[#007A8A]/40 text-[#007A8A] hover:bg-[#00E5FF]/15`} data-testid="header-student-login-btn">
            <UserRound className="w-4 h-4" />{student ? L("My portal", "मेरा पोर्टल") : L("Login", "लॉगिन")}
          </Link>
          <Link to="/enroll" className="hidden md:inline-flex">
            <Button className="rs-cta rounded-full px-5" data-testid="header-enroll-btn">{L("Enroll", "दाखिला")}</Button>
          </Link>
          <button
            onClick={() => setOpen((v) => !v)}
            className="min-[1440px]:hidden p-2.5 -mr-2 rounded-md hover:bg-black/5"
            aria-label="menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className={`min-[1440px]:hidden max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain ${isDark ? "bg-[#F0F4F8]" : "bg-white"} border-t border-black/10`} data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-5 py-3 grid gap-1">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-md text-[15px] ${
                    isActive ? "bg-[#FF3B3B] text-white" : "hover:bg-black/5"
                  }`
                }
                data-testid={`m-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                {L(n.label, n.hi)}
              </NavLink>
            ))}
            <Link to={portal} onClick={() => setOpen(false)}>
              <Button variant="outline" className="w-full mt-2 bg-transparent border-[#00E5FF]/40 text-[#007A8A]" data-testid="m-student-login-btn"><UserRound className="w-4 h-4 mr-1" />{student ? L("My student portal", "मेरा छात्र पोर्टल") : L("Student Login", "छात्र लॉगिन")}</Button>
            </Link>
            <Link to="/get-app" onClick={() => setOpen(false)}>
              <Button variant="outline" className="w-full mt-2 bg-transparent" data-testid="m-get-app-btn"><Smartphone className="w-4 h-4 mr-1" />{L("Download the App", "ऐप डाउनलोड करें")}</Button>
            </Link>
            <Link to="/enroll" onClick={() => setOpen(false)}>
              <Button className="w-full rs-cta mt-2" data-testid="m-enroll-btn">{L("Enroll Now", "अभी दाखिला लें")}</Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
